import { browser } from '$app/environment';
import { check, checkPresent } from '@yp/common/core.ts';
import { isPresent, pare, parseJson } from '@yp/common/identity.ts';
import type { StrictPropertyList } from '@yp/common/schema/descriptor.ts';
import { coerce } from '@yp/common/schema/validate.ts';
import type { StructuredStore } from '@yp/common/storage.ts';
import { nameOf, type NameStyle } from '@yp/web.app/auth/names.ts';
import { prefsStore } from '@yp/web.app/config/store.ts';
import { useObservable } from '@yp/web.app/hooks/observable.svelte.ts';
import { Session } from '@yp/web.firebase/context.ts';
import { getContext, setContext } from 'svelte';
import { isUserAdmin, isUserAtParty } from './admin.ts';

export class UserContext {
  private readonly session_ = useObservable(Session, { debug: false });

  //
  // Session-related data
  //

  get session() {
    return this.session_.v;
  }
  get user() {
    return this.session_.v?.user;
  }
  get claims() {
    return this.session_.v?.claims;
  }

  // When the app starts, the login state is indeterminate pending some async work. UI that depends
  // on isLoggedIn can wait on this to avoid an unnecessary state transition during startup.
  get isSessionLoaded() {
    return !this.session_.loading;
  }

  get isAtParty() {
    return isPresent(this.user) && isUserAtParty(this.user);
  }
  get isLoggedIn() {
    return isPresent(this.user);
  }
  get isAdmin() {
    return isPresent(this.user) && isUserAdmin(this.user);
    // return this.claims?.isAdmin ?? false;
  }

  get name() {
    return this.nameCustom();
  }
  nameCustom(style: NameStyle = 'full-first', defaultName = 'Unknown') {
    return nameOf(undefined, style, this.user?.displayName ?? defaultName);
  }

  //
  // Advanced or internal use only
  //

  sessionObservable() {
    return this.session_;
  }
}

const userContextName = 'userContext';

export function initUserContext() {
  const ctx = setContext<UserContext>(userContextName, new UserContext());
  $effect(() => {
    if (ctx.isSessionLoaded) {
      console.log('Session', { user: ctx.user?.uid ?? false, ...ctx.claims });
    }
  });
  return ctx;
}

export function userContext() {
  return checkPresent(getContext<UserContext | undefined>(userContextName));
}

export type LocalPrefs = {};

export const LocalPrefsDescriptor: StrictPropertyList<LocalPrefs> = [];

export type SessionPrefs = {};

export const SessionPrefsDescriptor: StrictPropertyList<SessionPrefs> = [];

let localStore_: StructuredStore | undefined;
let sessionStore_: StructuredStore | undefined;

const PrefsKey = 'ephemeral';

function ensurePrefStores() {
  check(browser, () => 'Pref stores can only be used in the browser.');
  localStore_ ??= prefsStore();
  sessionStore_ ??= prefsStore(true);
  return { localStore: localStore_, sessionStore: sessionStore_ };
}

// TODO: Make StructuredStore parameterized based on schemas.
export function getPrefs(): { local: LocalPrefs; session: SessionPrefs } {
  const { localStore, sessionStore } = ensurePrefStores();
  // TODO: It seems like it should be easier to combine structuredstore and coerce.
  const lRaw = localStore.get(PrefsKey);
  const lJson = isPresent(lRaw) ? parseJson(lRaw) : undefined;
  const local = coerce(lJson, LocalPrefsDescriptor);
  const sRaw = sessionStore.get(PrefsKey);
  const sJson = isPresent(sRaw) ? parseJson(sRaw) : undefined;
  const session = coerce(sJson, SessionPrefsDescriptor);
  return { local: local.valid ?? {}, session: session.valid ?? {} };
}

// TODO: DeepPartial merging?
export function updatePrefs({
  local,
  session,
}: {
  local?: Partial<LocalPrefs>;
  session?: Partial<SessionPrefs>;
}) {
  const { localStore, sessionStore } = ensurePrefStores();
  // console.log('updatePrefs', prefs);
  const prefs = getPrefs();
  if (isPresent(local)) {
    const update: LocalPrefs = pare({ ...prefs.local, ...local });
    localStore.set(PrefsKey, JSON.stringify(update));
  }
  if (isPresent(session)) {
    const update: SessionPrefs = pare({ ...prefs.session, ...session });
    sessionStore.set(PrefsKey, JSON.stringify(update));
  }
}
